// This is the primary entrypoint for mounting the Preact bundle.
// It is loaded via a <script> tag from our generated snippets and can always
// expect to have a div#__3PLAY_PRIVATE_ACCESS_PLAYER__ to mount onto.
//
// It is also pulled in from the iframe version of the snippet (index.html).

import { h, render, Fragment } from "preact";
import "preact/devtools"; // just want the devtools bridge (~240B)
import { useState, useEffect } from "preact/hooks";
import RecordedContainer from "./ui/RecordedContainer";
import { SettingsProvider } from "./ui/SettingsContext";
import { ErrorBoundary, Footer } from "./ui/shared/components";
import { ParsedSettings, WindowSettings } from "./types/settings";
import getFontPreference from "./ui/helpers/getFontPreference";
import { fetchData } from "./ui/shared";
import { RootDataType } from "./types/rootDataTypes";

import { getBundleURL } from "./ui/helpers/getBundleURL";

import { recordedEndpoint } from "./ui/helpers/routes";

import globalCss from "./ui/global.module.scss";
import cssReset from "bundle-text:./ui/reset.css";
import { parseQueryBoolean } from "./snippets/util";
import { Loading } from "./ui/shared/components/Loading";

const shadowDomAvailable = "attachShadow" in document.head;

declare global {
  interface Window {
    __3PLAY_ACCESS_PLAYER_SETTINGS__: WindowSettings;
    __PRIVATE_REMOUNT_THREEPLAY_ACCESS_PLAYER: () => void;
  }
}

function initializeApp() {
  const pluginEl =
    document.querySelector(
      '[data-threeplay-access-player="1"] [data-mount="1"]'
    ) ??
    (document.getElementById("__3PLAY_PRIVATE_ACCESS_PLAYER__") as Element);

  let appRoot = pluginEl;

  if (shadowDomAvailable) {
    const shadow = pluginEl.attachShadow({ mode: "open" });
    appRoot = document.createElement("div");
    appRoot.id = "shadow-root";

    shadow.appendChild(appRoot);
  }

  /**
   * get parent site font to see if on of plugins
   * available fonts should be preferred
   */
  const fontPreference = getFontPreference(
    getComputedStyle(appRoot).getPropertyValue("font-family")
  );

  function getDataAttributes(): WindowSettings | null {
    const element = document.querySelector(
      '[data-threeplay-access-player="1"]'
    ) as HTMLElement;

    if (!element) {
      return null;
    }

    // TODO typeguard
    return { ...element.dataset } as WindowSettings;
  }

  function getWindowSettings(): WindowSettings {
    return {
      ...window.__3PLAY_ACCESS_PLAYER_SETTINGS__,
      inIframe: Boolean(window.__3PLAY_ACCESS_PLAYER_SETTINGS__.inIframe),
    };
  }

  const defaultSettings = {
    platform: "native",
    theme: "auto",
    playAs: "infer",
  } as const;

  const DEFAULT_SHOW_TRANSCRIPT = true;
  const DEFAULT_SHOW_DOWNLOAD = true;
  const DEFAULT_SHOW_AD = true;
  const DEFAULT_SHOW_LANGUAGE_DROPDOWN = false; // hiding this for now; product wants to put it behind billing

  const Plugin = () => {
    // TODO: render failure state with lookup params.
    const windowSettings = getDataAttributes() ?? getWindowSettings();
    const settings: ParsedSettings = {
      ...defaultSettings,
      ...windowSettings,
      inIframe: Boolean(windowSettings.inIframe),
      showTranscript: parseQueryBoolean(
        windowSettings.showTranscript,
        DEFAULT_SHOW_TRANSCRIPT
      ),
      showDownload: parseQueryBoolean(
        windowSettings.showDownload,
        DEFAULT_SHOW_DOWNLOAD
      ),
      showLanguageDropdown: parseQueryBoolean(
        windowSettings.showLanguageDropdown,
        DEFAULT_SHOW_LANGUAGE_DROPDOWN
      ),
      showAd: parseQueryBoolean(windowSettings.showAd, DEFAULT_SHOW_AD),
    };

    const [rootData, setRootData] = useState<RootDataType>();
    const [failed, setFailed] = useState(false);

    type RootResponse = {
      data: RootDataType;
    };

    const getRootData = async (url: URL) => {
      setFailed(false);

      const { data, error } = await fetchData<RootResponse>(url);
      if (!error && data) {
        setRootData(data.data);
      }

      setFailed(Boolean(error));
    };

    useEffect(() => {
      getRootData(recordedEndpoint(settings, "/"));
    }, []);

    const cssFile = `${getBundleURL()}index.css`;

    /**
     * generate plugin aria label based on features available
     */
    let ariaLabel;
    if (rootData) {
      const adAvailable =
        settings.showAd &&
        Object.keys(rootData.available_audio_description_languages).length > 0;

      const transcriptAvailable =
        settings.showTranscript &&
        Object.keys(rootData.available_transcript_languages).length > 0;

      const playAs = "[audio/video]"; // TODO: add media type to root data
      const title = rootData.name;

      ariaLabel = `${
        adAvailable && transcriptAvailable
          ? `Audio description and Transcript are`
          : adAvailable && !transcriptAvailable
          ? `Audio description is`
          : !adAvailable && transcriptAvailable
          ? `Transcript is`
          : `No Audio Description or Transcript are`
      } available for the following ${playAs} file: ${title}`;
    }

    return (
      <SettingsProvider appRoot={pluginEl} initialSettings={settings}>
        {/*
        For old browsers that don't support the Shadow DOM isolation, we may
        have some visual issues but still support rendering.
      */}
        {shadowDomAvailable && <style>{cssReset}</style>}
        {/*
        This following style line is a weird import: let me explain.

        Parcel generates a index.css as an extracted file from all our module
        files. In a normal site, this would just be imported in the head.
        However, we are not a normal site, but a plugin hidden underneath a
        shadow DOM isolating layer.

        As such, we need to import the CSS from within the shadow DOM like
        we do here. This requires knowledge of the output path, which we can
        predict by using the version field in the package.json file.

        This means that versioning must always happen before build and publish,
        as the version is literally baked into our bundle for this lookup.
      */}
        <style>{`@import "${cssFile}";`}</style>
        <div
          class={`${globalCss.plugin} ${
            settings.inIframe ? "" : globalCss.shadow
          }`}
          style={`font-family: ${fontPreference}`}
        >
          <section
            aria-label={ariaLabel}
            data-testid="plugin-container"
            class={globalCss.section}
          >
            <ErrorBoundary>
              {failed ? (
                <div class={globalCss.loadingContainer}>
                  <p>
                    Failed to load the Access Player.{" "}
                    <a
                      class={globalCss.a}
                      onClick={() =>
                        getRootData(recordedEndpoint(settings, "/"))
                      }
                    >
                      Try again?
                    </a>
                  </p>
                </div>
              ) : rootData ? (
                <RecordedContainer {...rootData} />
              ) : (
                <div class={globalCss.loadingContainer}>
                  <Loading />
                </div>
              )}
            </ErrorBoundary>
          </section>
        </div>
      </SettingsProvider>
    );
  };

  render(<Plugin />, appRoot);
}

initializeApp();

// This is a backdoor way of remounting the component in exceptional
// circumstances. Namely, the Access Player Builder triggers remounts
// based on settings changes and this allows it to re-render even when this file
// has already been run (missing the side-effect of mounting the first time).
window.__PRIVATE_REMOUNT_THREEPLAY_ACCESS_PLAYER = initializeApp;
